import { getReaderChapterVoice, type ReaderBook } from '@/services/booksApi';
import { getOfflineSettings } from '@/services/offlineSettings';
import { readCachedCatalog } from '@/services/readerCacheStore';
import { isBookFinished } from '@/utils/audioProgressUtils';
import type { Book } from '@/utils/bookRouting';
import { getStoreUsage, listStoredAssets, type StoredAsset, type StoreUsage } from './assetStore';

/**
 * How big a whole-book download would be, and whether it fits.
 *
 * Declared sizes are a hint, not a guarantee: bundles ingested before the
 * contract carried byte counts have none, so most of what is in production
 * today declares nothing. Where a size is declared it is used; where it is
 * not, audio is estimated from its duration at the bundles' measured bit
 * rate, and text from a per-chapter allowance. A figure with any estimated
 * part is labelled "about", and a declared zero counts as absent — a size
 * preflight must never read it as "this asset is empty".
 *
 * Fit is judged the way the scheduler will act: room under the cap, plus
 * everything that evicting every finished, unpinned book would free. A
 * refusal here is the only time the user is told no; a download that turns
 * out larger than estimated merely stops at the cap and resumes when room
 * appears, which is the buffer's ordinary behaviour.
 */

/**
 * The bundles' MP3s are 192 kbps: 8,362,780 bytes for 348.4 s in the bundle
 * spec's example, and the same on the device pass. About 86 MB an hour.
 */
export const ESTIMATED_AUDIO_BYTES_PER_SECOND = 24_000;

/** Per chapter, when the bundle did not say. From the size survey: ~175 KB of text and sync. */
export const ESTIMATED_HTML_BYTES = 80 * 1024;
export const ESTIMATED_TOKEN_MAP_BYTES = 45 * 1024;
export const ESTIMATED_TIMINGS_BYTES = 50 * 1024;

export interface SizeEstimate {
  bytes: number;
  /** True when any part of the figure was estimated rather than declared. */
  estimated: boolean;
}

export interface DownloadAssessment extends SizeEstimate {
  /** Still to fetch: the book less what the store already holds for it. */
  neededBytes: number;
  /** Room under the cap, plus what evicting every finished, unpinned book would free. */
  availableBytes: number;
  fits: boolean;
}

/** What the control says when a book cannot fit. One sentence of guidance, no numbers. */
export const DOWNLOAD_REFUSED_MESSAGE =
  "There isn't room on this phone for this book. Remove another download, or clear downloads from your profile, to make space.";

const declared = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;

/** From the catalog alone, before the payload is fetched: enough for a label. */
export const estimateCatalogBookBytes = (book: Pick<Book, 'totalDuration'>): SizeEstimate => ({
  bytes: Math.round(Math.max(0, book.totalDuration) * ESTIMATED_AUDIO_BYTES_PER_SECOND),
  estimated: true,
});

/**
 * From the payload, for one voice. CSS and images are not counted: nothing
 * declares their sizes, and they are kilobytes against megabytes a chapter.
 */
export const estimateReaderBookBytes = (book: ReaderBook, voiceKey: string): SizeEstimate => {
  let bytes = 0;
  let estimated = false;
  const take = (value: number | null | undefined, fallback: number) => {
    const size = declared(value);
    if (size === null) {
      estimated = true;
      bytes += fallback;
    } else {
      bytes += size;
    }
  };
  for (const chapter of book.chapters) {
    take(chapter.htmlBytes, ESTIMATED_HTML_BYTES);
    const voice = getReaderChapterVoice(chapter, voiceKey);
    if (!voice) {
      // Read, not played: text only.
      continue;
    }
    take(chapter.tokenMapBytes, ESTIMATED_TOKEN_MAP_BYTES);
    take(voice.timingsBytes, ESTIMATED_TIMINGS_BYTES);
    take(voice.audioBytes, (Math.max(0, voice.durationMs) / 1000) * ESTIMATED_AUDIO_BYTES_PER_SECOND);
  }
  return { bytes: Math.round(bytes), estimated };
};

export interface FitInput {
  estimate: SizeEstimate;
  /** Bytes the store already holds for this book, in this voice. */
  heldBytes: number;
  usage: Pick<StoreUsage, 'bytes' | 'capBytes'>;
  /** Bytes of finished, unpinned books other than this one and the open one. */
  reclaimableBytes: number;
}

export const assessDownload = ({ estimate, heldBytes, usage, reclaimableBytes }: FitInput): DownloadAssessment => {
  const neededBytes = Math.max(0, estimate.bytes - heldBytes);
  const availableBytes = Math.max(0, usage.capBytes - usage.bytes) + Math.max(0, reclaimableBytes);
  return { ...estimate, neededBytes, availableBytes, fits: neededBytes <= availableBytes };
};

export interface PreflightDeps {
  listStoredAssets: () => StoredAsset[];
  getStoreUsage: () => Pick<StoreUsage, 'bytes' | 'capBytes'>;
  pinnedBookIds: () => string[];
  isBookFinished: (bookId: string) => boolean;
}

/**
 * Would this book, in this voice, fit on the phone? Reads the store and the
 * pins; the open book is named because the scheduler never evicts it, so
 * the preflight must not count on that space either.
 */
export const preflightDownload = (
  book: ReaderBook,
  voiceKey: string,
  openBookId: string | null,
  deps: PreflightDeps = defaultPreflightDeps,
): DownloadAssessment => {
  const pinned = new Set(deps.pinnedBookIds());
  const finished = new Map<string, boolean>();
  let heldBytes = 0;
  let reclaimableBytes = 0;
  for (const asset of deps.listStoredAssets()) {
    if (asset.bookId === book.id) {
      // Another voice's files do not shorten this voice's download.
      if (asset.voiceKey === null || asset.voiceKey === voiceKey) {
        heldBytes += asset.bytes;
      }
      continue;
    }
    if (asset.bookId === openBookId || pinned.has(asset.bookId)) {
      continue;
    }
    let evictable = finished.get(asset.bookId);
    if (evictable === undefined) {
      evictable = deps.isBookFinished(asset.bookId);
      finished.set(asset.bookId, evictable);
    }
    if (evictable) {
      reclaimableBytes += asset.bytes;
    }
  }
  return assessDownload({
    estimate: estimateReaderBookBytes(book, voiceKey),
    heldBytes,
    usage: deps.getStoreUsage(),
    reclaimableBytes,
  });
};

const MB = 1024 * 1024;
const GB = 1024 * MB;

/** "about 860 MB", "1.2 GB": the size as a label, hedged when any part was estimated. */
export const formatDownloadSize = ({ bytes, estimated }: SizeEstimate): string => {
  let figure: string;
  if (bytes >= GB) {
    figure = `${(bytes / GB).toFixed(1)} GB`;
  } else if (bytes >= 100 * MB) {
    figure = `${Math.round(bytes / MB)} MB`;
  } else if (bytes >= MB) {
    figure = `${(bytes / MB).toFixed(1)} MB`;
  } else {
    figure = `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }
  return estimated ? `about ${figure}` : figure;
};

const catalogBook = (bookId: string): Book | null => readCachedCatalog()?.find((book) => book.id === bookId) ?? null;

export const defaultPreflightDeps: PreflightDeps = {
  listStoredAssets,
  getStoreUsage,
  pinnedBookIds: () => getOfflineSettings().pinnedBooks.map((pin) => pin.bookId),
  // A book the catalog does not know cannot be judged finished, so its
  // space is never counted on — the same rule the scheduler evicts by.
  isBookFinished: (bookId) => {
    const book = catalogBook(bookId);
    return book !== null && isBookFinished(book);
  },
};
